/**
 * Streak Analysis Component
 * Displays current and longest win/loss streaks in a DataTable
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { BackendTradeStatistics, TradeStatisticsFilters } from "../../../../types/trade";
import { StatisticsCardHeadline } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { useDailyActivity } from "../../../../hooks/useTradeStatistics";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { calculateTradingDayStreaks } from "../../../../utils/tradingDayStreaks";
import { DataTable } from "../../../../components/ui/data-table";
import { DashboardWidgetLoadingCard } from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { isStatisticsFetchPending } from "./statisticsEmptyState";

interface StreakAnalysisProps {
  statistics: BackendTradeStatistics | null;
  filters?: TradeStatisticsFilters;
  loading?: boolean;
}

interface StreakRow {
  metric: string;
  value: number;
  type: "win" | "loss" | "trading";
}

const StreakAnalysis: React.FC<StreakAnalysisProps> = ({
  statistics,
  filters,
  loading = false,
}) => {
  const displayTimeZone = useDisplayTimeZone();

  const dailyActivityFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!filters) return undefined;
    return { ...filters, period: "daily" };
  }, [filters]);

  const { data: dailyActivity, loading: dailyActivityLoading } =
    useDailyActivity(dailyActivityFilters);

  const dayStreaks = useMemo(() => {
    if (!dailyActivity?.daily_data_points?.length) {
      return calculateTradingDayStreaks([], displayTimeZone);
    }
    return calculateTradingDayStreaks(dailyActivity.daily_data_points, displayTimeZone);
  }, [dailyActivity, displayTimeZone]);

  const streakData: StreakRow[] = useMemo(() => {
    const streaks = statistics
      ? {
          currentWinStreak: statistics.current_win_streak,
          currentLossStreak: statistics.current_loss_streak,
          longestWinStreak: statistics.longest_win_streak,
          longestLossStreak: statistics.longest_loss_streak,
        }
      : { currentWinStreak: 0, currentLossStreak: 0, longestWinStreak: 0, longestLossStreak: 0 };

    return [
      { metric: "Current Win Streak", value: streaks.currentWinStreak, type: "win" as const },
      { metric: "Current Loss Streak", value: streaks.currentLossStreak, type: "loss" as const },
      { metric: "Longest Win Streak", value: streaks.longestWinStreak, type: "win" as const },
      { metric: "Longest Loss Streak", value: streaks.longestLossStreak, type: "loss" as const },
      { metric: "Current Consecutive Days Traded", value: dayStreaks.currentDaysTradedStreak, type: "trading" as const },
      { metric: "Longest Consecutive Days Traded", value: dayStreaks.longestDaysTradedStreak, type: "trading" as const },
      { metric: "Current Consecutive Winning Days", value: dayStreaks.currentWinningDaysStreak, type: "win" as const },
      { metric: "Longest Consecutive Winning Days", value: dayStreaks.longestWinningDaysStreak, type: "win" as const },
    ];
  }, [statistics, dayStreaks]);

  const columns: ColumnDef<StreakRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  if (loading || isStatisticsFetchPending(statistics) || dailyActivityLoading) {
    return (
      <DashboardWidgetLoadingCard
        cardType="streakAnalysis"
        variant="table"
        className="h-full min-h-0 flex-1"
      />
    );
  }

  return (
    <div
      data-statistics-shadcn-card
      className="flex h-full min-h-0 flex-1 flex-col rounded-[6px] border border-border bg-background p-4"
    >
      <div className="mb-4">
        <StatisticsCardHeadline>Streak Analysis</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={streakData}
        showPagination={false}
        emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
      />
    </div>
  );
};

export default StreakAnalysis;
